import { useMemo, useCallback } from "react"
import Echart from "echarts-for-react"
import { usageChartStore } from "../usage-chart-store"
import { langStore } from "@/stores/lang-store"
import { useSearchParams } from "react-router-dom"
import { RESOURCE_META } from "@/constants/resource-const"
import { usageChartBundle } from "../usage-const"

const UsageChart = ({ xLabels = [], series = [] }) => {
  const { setRange, setSummary, unit } = usageChartStore()
  const [searchParams] = useSearchParams()
  const resource = searchParams.get("resource") ?? "water"

  const { lang } = langStore()

  const option = useMemo(() => {
    if (!xLabels.length || !series.length) return null

    const chartLabels = xLabels.map((label) => `${label}${usageChartBundle.time[lang]}`)

    return {
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          let result = `<div style="font-weight: bold margin-bottom: 4px;">${params[0].name}</div>`
          params.forEach((item) => {
            const color = item.borderColor || item.color
            const value = item.value.toLocaleString()

            result += `
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px;">
                <span style="display: flex; align-items: center;">
                  <span style="display:inline-block; margin-right:5px; border-radius:10px; width:10px; height:10px; background-color:${color};"></span>
                  ${item.seriesName}
                </span>
                <span style="font-weight: bold;">${value} ${unit}</span>
              </div>
            `
          })
          return result
        }
      },
      dataZoom: [
        { type: "inside" },
        {
          show: false,
          showDetail: false,
          showDataShadow: false,
          handleSize: 40,
          handleStyle: { color: "#182a87", borderWidth: 0 },
          backgroundColor: "#f1f1f1",
          fillerColor: "#4caefe33",
          borderColor: "transparent",
          brushSelect: false,
          moveHandleSize: 0
        }
      ],
      legend: { top: 0 },
      grid: { left: "3%", right: "2%", bottom: 0, top: 40, containLabel: true },
      xAxis: {
        type: "category",
        data: chartLabels,
        boundaryGap: false,
        axisLabel: { rotate: 0, interval: "auto", hideOverlap: true }
      },
      yAxis: { type: "value" },
      series: series.map((s) => ({
        name: usageChartBundle.info[s.name][lang],
        type: "line",
        data: s.data,
        smooth: true,
        showSymbol: false,
        symbol: "circle",
        itemStyle: { color: "#fff", borderColor: s.color, borderWidth: 2 },
        lineStyle: { width: 2, color: s.color },
        areaStyle:
          s.color === RESOURCE_META["water"]["leak"].colors.primary
            ? { color: s.color, opacity: 0.08 }
            : null
      }))
    }
  }, [xLabels, series, lang, unit])

  // 2. 이벤트 핸들러 메모이제이션
  const onDataZoom = useCallback(
    (params) => {
      const zoom = params.batch?.[0] || params
      const start = Math.floor((zoom.start / 100) * xLabels.length)
      const end = Math.ceil((zoom.end / 100) * xLabels.length)

      setRange(start, end, xLabels)

      const usage = series[0]?.data.slice(start, end).reduce((a, b) => a + b, 0) || 0

      let leak = 0
      // series[1]이 존재할 때만 계산 (에러 방지)
      if (resource === "water" && series[1]) {
        leak = series[1].data.slice(start, end).reduce((a, b) => a + b, 0) || 0
      }

      setSummary({ usage, leak })
    },
    [xLabels, series, setRange, setSummary, resource]
  )

  if (!xLabels.length || !series.length) return null

  return (
    <Echart
      style={{ height: "100%" }}
      option={option}
      notMerge={true} // 데이터 개수가 변하므로(2개->1개) true 권장
      onEvents={{ datazoom: onDataZoom }}
    />
  )
}

export default UsageChart
